import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

/**
 * HTTP Interceptor that adds breadcrumbs and tracking for HTTP requests
 * This provides visibility into API calls and their performance in Sentry traces
 */
@Injectable()
export class SentryTraceInterceptor implements HttpInterceptor {
  private slowRequests: string[] = [];
  private readonly MAX_SLOW_REQUESTS = 50; // Prevent unbounded growth
  private readonly SLOW_THRESHOLD = 1000;
  private readonly VERY_SLOW_THRESHOLD = 3000;

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const url = req.url;
    const method = req.method;
    if (this.shouldSkipTracking(url)) {
      return next.handle(req);
    }
    const startTime = performance.now();
    const sanitizedUrl = this.sanitizeUrl(url);

    /* ╔════════════════════════════════════════════════════════════════════════════╗
     * ║  ℹ️  PERFORMANCE TRACING: HTTP Request Spans (Currently Disabled)         ║
     * ╠════════════════════════════════════════════════════════════════════════════╣
     * ║  Each HTTP request can be traced as a Sentry span for detailed            ║
     * ║  performance monitoring. This provides:                                    ║
     * ║  • Request/response duration tracking                                      ║
     * ║  • HTTP status code capture                                                ║
     * ║  • Error state monitoring                                                  ║
     * ║  • Integration with distributed tracing                                    ║
     * ║                                                                             ║
     * ║  To enable: Uncomment the span creation and attribute code below.          ║
     * ║  Note: This may increase Sentry quota usage significantly.                 ║
     * ╚════════════════════════════════════════════════════════════════════════════╝
     */

    // Uncomment to enable Sentry span tracing for this HTTP request:
    // const span = Sentry.startInactiveSpan({
    //   op: 'http.client',
    //   name: `${method} ${sanitizedUrl}`,
    //   attributes: {
    //     'http.method': method,
    //     'http.url': sanitizedUrl,
    //   },
    // });

    let statusCode: number | undefined;
    return next.handle(req).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            statusCode = event.status;
            // Uncomment to add HTTP status to span:
            // span?.setAttributes({
            //   'http.status_code': statusCode,
            // });
          }
        },
        error: (error) => {
          statusCode = error.status || 500;
          // Uncomment to record error details on span:
          // span?.setStatus({ code: 2, message: error.message }); // 2 = ERROR
          // span?.setAttributes({
          //   'http.status_code': statusCode,
          //   error: true,
          // });

          Sentry.addBreadcrumb({
            category: 'http',
            message: `${method} ${sanitizedUrl}`,
            level: 'error',
            data: {
              status: statusCode,
              error: error.message,
            },
          });
        },
      }),
      finalize(() => {
        const duration = performance.now() - startTime;
        const isSlowRequest = duration > this.SLOW_THRESHOLD;
        const isVerySlowRequest = duration > this.VERY_SLOW_THRESHOLD;

        // Uncomment to finalize span with duration and response data:
        // span?.setAttributes({
        //   'http.response_content_length': 0, // Add if available
        //   duration_ms: duration,
        // });
        // span?.end();

        Sentry.addBreadcrumb({
          category: 'http',
          message: `${method} ${sanitizedUrl}`,
          level: isSlowRequest ? 'warning' : 'info',
          data: {
            duration,
            status: statusCode,
            slow: isSlowRequest,
          },
        });

        if (isVerySlowRequest) {
          const requestInfo = `${method} ${sanitizedUrl} [${statusCode}] ${duration.toFixed(0)}ms`;
          // Limit array size to prevent memory leak
          if (this.slowRequests.length >= this.MAX_SLOW_REQUESTS) {
            this.slowRequests.shift();
          }
          this.slowRequests.push(requestInfo);
          Sentry.addBreadcrumb({
            category: 'performance',
            message: `⚠️ Very slow request: ${method} ${sanitizedUrl}`,
            level: 'warning',
            data: {
              duration,
              status: statusCode,
            },
          });
        }
      }),
    );
  }

  /**
   * Determine if this URL should be tracked
   */
  private shouldSkipTracking(url: string): boolean {
    // Skip path patterns
    const skipPatterns = [
      '/assets/',
      '/i18n/',
      '.json',
      '.svg',
      '.png',
      '.jpg',
      '.ico',
      '/health',
      '/ping',
    ];

    const lowerUrl = url.toLowerCase();

    // Check path patterns
    if (skipPatterns.some((pattern) => lowerUrl.includes(pattern))) {
      return true;
    }

    return false;
  }

  private sanitizeUrl(url: string): string {
    try {
      const urlObj = new URL(url, window.location.origin);
      // Remove query parameters that might contain sensitive data
      const sensitiveParams = ['token', 'password', 'secret', 'key', 'auth'];
      sensitiveParams.forEach((param) => {
        if (urlObj.searchParams.has(param)) {
          urlObj.searchParams.set(param, '[FILTERED]');
        }
      });
      return urlObj.pathname + (urlObj.search ? urlObj.search : '');
    } catch {
      // If URL parsing fails, just return the original
      return url;
    }
  }
}
